import { SectionCard } from '@/components/common/SectionCard';
import { toHttpHref } from '@/lib/utils/url.utils';
import type { EventLocationType } from '@/lib/types/events.types';
import { EVENTS_STRINGS } from '../constants/events-strings';

const S = EVENTS_STRINGS.GUEST;

/** What the value is called, by location type; an event with none reads as a link. */
const LABEL_BY_LOCATION: Record<EventLocationType, string> = {
  virtual: S.JOIN_LINK_LABEL,
  in_person: S.JOIN_ADDRESS_LABEL,
};

interface JoinDetailsPanelProps {
  locationType: EventLocationType | null;
  locationDetails: string;
  className?: string;
}

/**
 * How a registered viewer joins (FR-286 for a non-member, FR-292 for a member): the
 * event's join link or address.
 *
 * The value is the admin's free text and reaches this page from an API read, so
 * it is rendered as a link only when the whole value is one http(s) URL, as the
 * confirmation email does (design section 41). Anything else, including a
 * `javascript:` URL or markup, is plain text: React escapes it, and no HTML is
 * ever injected.
 */
export function JoinDetailsPanel({ locationType, locationDetails, className }: JoinDetailsPanelProps) {
  const href = toHttpHref(locationDetails);
  const label = locationType ? LABEL_BY_LOCATION[locationType] : S.JOIN_LINK_LABEL;
  return (
    <SectionCard title={S.JOIN_TITLE} className={className}>
      <p className="text-xs font-semibold uppercase tracking-wide text-teal2">{label}</p>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-block break-all text-[1rem] font-medium text-green underline hover:text-dkgreen"
        >
          {locationDetails.trim()}
        </a>
      ) : (
        <p className="mt-1 max-w-[47.5rem] whitespace-pre-line break-words text-[1rem] text-subtext">
          {locationDetails}
        </p>
      )}
    </SectionCard>
  );
}
